import {
	SignedIn,
	SignedOut,
	SignInButton,
	UserButton,
	useUser,
} from "@clerk/clerk-react";
import React, { type ReactNode, useState } from "react";
import "@/components/_shared/styles/animations.css"; // Keep animations if needed elsewhere, but bg is removed
import {
	ArrowLeft,
	Github,
	Lightbulb,
	Linkedin,
	Moon,
	Sun,
	Trash2,
} from "lucide-react";
import { Link } from "wouter";
import { useGlobalDarkMode } from "@/app/layout"; // Updated import path
// Removed usePageSettings import
// IdeenModal importieren, falls noch nicht geschehen (angenommen, sie ist in einem Pfad wie "@/components/startseite/IdeenModal")
import { IdeenModal } from "@/components/startseite/IdeenModal"; // Pfad anpassen, falls nötig
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface PageLayoutProps {
	children: ReactNode;
	title?: string;
	toolId?: string;
	// onHelpClick?: () => void; // Entfernt, da der Button entfernt wird
	additionalItemsToDelete?: string[];
	confirmationText?: string;
	isStartseite?: boolean;
	backUrl?: string;
	// hideDelete?: boolean; // Entfernt, da der Button entfernt wird
	extraHeaderButton?: ReactNode;
}

/**
 * Gemeinsames Layout für alle Seiten, inklusive Startseite.
 * Integriert Header und Footer.
 */
export default function PageLayout({
	children,
	title,
	toolId,
	// onHelpClick, // Entfernt, da der Button und das Prop entfernt wurden
	additionalItemsToDelete = [],
	confirmationText,
	isStartseite = false,
	backUrl,
	// hideDelete, // Entfernt
	extraHeaderButton,
}: PageLayoutProps) {
	const { darkMode, toggleDarkMode } = useGlobalDarkMode();
	const { user } = useUser(); // user-Objekt für Header-Infos
	const [showIdeenModal, setShowIdeenModal] = useState(false); // State für IdeenModal

	// Logik für den Titel im Header - wird nicht mehr direkt so verwendet
	// const headerTitle = isStartseite ? "labi.dev" : title || "labi.dev";
	// const headerLink = isStartseite || !backUrl ? "/" : backUrl; // Nicht mehr benötigt, da Zurück-Knopf immer zu / geht

	return (
		<div className="container relative z-10 mx-auto max-w-5xl px-4 py-5">
			{/* Einheitlicher Header für alle Seiten */}
			<header className="mb-6 flex items-center justify-between border-b p-4 dark:border-gray-700">
				{/* Linke Seite: "labi.dev" */}
				<div className="flex items-center">
					{" "}
					{/* space-x entfernt, da nur noch labi.dev hier ist */}
					{/* Zurück-Knopf entfernt */}
					{/* {!isStartseite && (
<Link href="/">
<Button
type="button"
variant="outline"
size="icon"
className="text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 dark:text-gray-300 dark:hover:text-indigo-400 dark:hover:bg-indigo-900/20 border-gray-200 dark:border-gray-700"
title="Zurück zur Startseite"
>
<ArrowLeft className="h-5 w-5" />
</Button>
</Link>
)} */}
					<Link
						className="labi-dev-animate font-bold text-4xl transition-colors"
						href="/"
					>
						labi.dev
					</Link>
					{/* Seitentitel wird nicht mehr angezeigt */}
					{/* {!isStartseite && title && (
<>
<span className="text-gray-400 dark:text-gray-500 hidden sm:inline">
|
</span>
<span className="font-semibold text-lg text-gray-700 dark:text-gray-200 hidden sm:inline">
{title}
</span>
</>
)} */}
					{/* Benutzerinfo nicht mehr links */}
					{/* <SignedIn>
{user && (
<div className="flex items-center space-x-2 ml-2">
<img
src={user.imageUrl}
alt="Profilbild"
className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-600"
/>
<span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden sm:inline">
{user.firstName} {user.lastName}
</span>
</div>
)}
</SignedIn> */}
				</div>

				{/* Rechte Seite: Aktionsbuttons */}
				<div className="flex items-center space-x-3">
					<Button
						className="border-gray-200 text-gray-600 hover:bg-amber-50 hover:text-amber-600 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-amber-900/20 dark:hover:text-amber-400"
						onClick={() => setShowIdeenModal(true)}
						size="icon"
						title="Idee einreichen"
						type="button"
						variant="outline"
					>
						<Lightbulb className="h-5 w-5" />
					</Button>

					{extraHeaderButton}

					<Button
						className="border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-50"
						onClick={toggleDarkMode}
						size="icon"
						title={darkMode ? "Light Mode aktivieren" : "Dark Mode aktivieren"}
						type="button"
						variant="outline"
					>
						<div className="relative flex h-5 w-5 items-center justify-center">
							<Sun
								className={`absolute inset-0 m-auto h-5 w-5 transform transition-all duration-300 ease-in-out ${
									darkMode
										? "rotate-90 scale-0 opacity-0"
										: "rotate-0 scale-100 opacity-100"
								}`}
							/>
							<Moon
								className={`absolute inset-0 m-auto h-5 w-5 transform transition-all duration-300 ease-in-out ${
									darkMode
										? "rotate-0 scale-100 opacity-100"
										: "-rotate-90 scale-0 opacity-0"
								}`}
							/>
						</div>
					</Button>

					{/* Trennstrich */}
					<div className="mx-1 h-6 w-px self-center bg-gray-300 dark:bg-gray-600" />

					{/* UserButton und SignInButton direkt im Flex-Container für bessere vertikale Ausrichtung */}
					<SignedIn>
						{/* Nur noch UserButton, der Bild anzeigt und Overlay öffnet */}
						<UserButton afterSignOutUrl="/" />
					</SignedIn>
					<SignedOut>
						<SignInButton mode="modal">
							<Button
								className="border-gray-200 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400"
								size="sm"
								variant="outline"
							>
								Anmelden
							</Button>
						</SignInButton>
					</SignedOut>
				</div>
			</header>

			{/* Main content */}
			{children}

			{/* Footer for all pages */}
			<footer className="mt-16 border-gray-200 border-t pt-6 pb-4 dark:border-gray-800">
				<div className="flex flex-col items-center justify-between md:flex-row">
					<div className="mb-4 md:mb-0">
						<p className="text-gray-600 text-sm dark:text-gray-400">
							© {new Date().getFullYear()} - Sven Labitzki
						</p>
					</div>

					<div className="flex items-center space-x-6">
						<a
							className="text-gray-500 transition-colors hover:text-indigo-500 dark:text-gray-400 dark:hover:text-indigo-400"
							href="https://github.com/xuoon"
							rel="noopener noreferrer"
							target="_blank"
							title="GitHub"
						>
							<Github className="h-5 w-5" />
						</a>

						<a
							className="text-gray-500 transition-colors hover:text-indigo-500 dark:text-gray-400 dark:hover:text-indigo-400"
							href="https://de.linkedin.com/in/sven-labitzki"
							rel="noopener noreferrer"
							target="_blank"
							title="LinkedIn"
						>
							<Linkedin className="h-5 w-5" />
						</a>
					</div>
				</div>
			</footer>

			{/* Ideen Modal */}
			<IdeenModal
				isOpen={showIdeenModal}
				onClose={() => setShowIdeenModal(false)}
			/>
		</div> // Closing tag for the container div
	);
}
